/**
 * Helper functions for the Language Selector component
 * @fileoverview Utility functions for filtering and managing language data
 */

import { type Language, type LanguageData } from '../LanguageSelector.types';

/**
 * Filters language data based on available languages
 * @param languageData - Complete array of language data
 * @param languages - Optional array of languages to filter by
 * @returns Filtered array of language data
 * @example
 * ```typescript
 * const filtered = filterLanguageData(allLanguages, ['en', 'fr']);
 * // Returns only English and French language options
 * ```
 */
export const filterLanguageData = (
  languageData: LanguageData[],
  languages?: Language[],
): LanguageData[] => {
  if (!languages?.length) {
    return languageData;
  }

  const languageSet = new Set(languages);
  return languageData.filter((item) => languageSet.has(item.language));
};

/**
 * Finds the selected language data from available options
 * @param languageData - Array of available language data
 * @param selectedLanguage - Currently selected language code
 * @returns Language data for selected language, or fallback to English
 * @example
 * ```typescript
 * const selected = findSelectedLanguageData(languages, 'fr');
 * // Returns French language data or English as fallback
 * ```
 */
export const findSelectedLanguageData = (
  languageData: LanguageData[],
  selectedLanguage?: Language,
): LanguageData => {
  return (
    languageData.find(({ language }) => language === selectedLanguage) ??
    languageData.find(({ language }) => language === 'en')!
  );
};
